import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/site";
import { NAV } from "@/constants/testIds";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={NAV.root}
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-brand-line shadow-[0_1px_0_rgba(31,26,28,0.04)]"
          : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link
            to="/"
            data-testid={NAV.logo}
            className="flex items-center gap-3 group"
          >
            <img
              src={SITE.logo}
              alt="Embrace Healthcare logo"
              className="h-12 lg:h-14 w-auto"
            />
            <span className="sr-only">{SITE.name}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-2" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.id}
                to={link.to}
                end={link.to === "/"}
                data-testid={NAV.link(link.id)}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-[15px] font-medium transition-colors duration-200 ${
                    isActive
                      ? "text-brand bg-brand-soft"
                      : "text-brand-ink hover:text-brand hover:bg-brand-soft/60"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={SITE.phoneHref}
              data-testid={NAV.callCta}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand text-white px-5 py-3 text-sm font-semibold hover:bg-brand-hover transition-all duration-300 hover:-translate-y-0.5 shadow-sm"
            >
              <Phone className="w-4 h-4" strokeWidth={2} />
              <span>{SITE.phone}</span>
            </a>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid={NAV.mobileToggle}
              aria-label="Toggle navigation"
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-brand-line text-brand-ink hover:bg-brand-soft transition"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          data-testid={NAV.mobilePanel}
          className="lg:hidden border-t border-brand-line bg-white"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.id}
                to={link.to}
                end={link.to === "/"}
                data-testid={NAV.mobileLink(link.id)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base font-medium ${
                    isActive
                      ? "text-brand bg-brand-soft"
                      : "text-brand-ink hover:bg-brand-soft/60"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <a
              href={SITE.phoneHref}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-5 py-3 text-base font-semibold"
            >
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
